import React, { useState } from 'react';
import { Form, Formik, FieldArray } from 'formik';
import { Button, Card, Badge } from 'react-bootstrap';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import ReactSwitch from 'react-switch';
import Loader from '../../common/UI/Loader';
import SimpleImageUpload from './SimpleImageUpload';
import ThemeEditor from './ThemeEditor';

// For sections like: how_it_works, comparison
const NestedSectionEditor = ({ section, backToList, refreshSections }) => {
  const [loading, setLoading] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        sectionKey: section.sectionKey,
        sectionName: section.sectionName,
        sectionType: 'nested',
        config: values.config,
      };

      await homepageApi.upsertHomepageSection(payload);
      toastSuccess('Section updated successfully');
      refreshSections();
    } catch (error) {
      toastError('Failed to update section');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Render based on section type
  const renderEditor = (formik) => {
    if (section.sectionKey === 'how_it_works') {
      return (
        <>
          {/* Section Title */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-heading me-2"></i>Section Title
              </h5>
            </Card.Header>
            <Card.Body>
              <CustomInput
                name="config.title"
                label="Section Title"
                placeholder="How it Works"
                className="form-control"
                labelclassname="form-label fw-bold"
              />
            </Card.Body>
          </Card>

          {/* Steps Section */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-list-ol me-2"></i>Steps
              </h5>
              <Badge bg="light" text="dark">
                {formik.values.config?.steps?.length || 0} steps
              </Badge>
            </Card.Header>
            <Card.Body>
              <FieldArray name="config.steps">
                {({ push, remove, move }) => (
                  <>
                    {formik.values.config?.steps?.map((step, index) => (
                      <Card key={index} className="mb-3 border-start border-4 border-info">
                        <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <Badge bg="info" pill>
                              Step {index + 1}
                            </Badge>
                            <span className="text-muted">
                              {step.status ? (
                                <Badge bg="success">Active</Badge>
                              ) : (
                                <Badge bg="secondary">Inactive</Badge>
                              )}
                            </span>
                          </div>
                          <div className="d-flex gap-2">
                            {index > 0 && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => move(index, index - 1)}
                                title="Move up"
                              >
                                <i className="fas fa-arrow-up"></i>
                              </Button>
                            )}
                            {index < formik.values.config.steps.length - 1 && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => move(index, index + 1)}
                                title="Move down"
                              >
                                <i className="fas fa-arrow-down"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to remove this step?')) {
                                  remove(index);
                                }
                              }}
                              title="Remove step"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <div className="row">
                            <div className="col-md-2">
                              <CustomInput
                                name={`config.steps.${index}.stepNumber`}
                                label="Step Number"
                                type="number"
                                placeholder="1"
                                className="form-control mb-2"
                                labelclassname="form-label fw-bold"
                              />
                            </div>
                            <div className="col-md-2">
                              <CustomInput
                                name={`config.steps.${index}.order`}
                                label="Display Order"
                                type="number"
                                placeholder="0"
                                className="form-control mb-2"
                                labelclassname="form-label fw-bold"
                              />
                            </div>
                            <div className="col-md-2">
                              <label className="form-label fw-bold">Active Status</label>
                              <div>
                                <ReactSwitch
                                  checked={step.status}
                                  onChange={(checked) =>
                                    formik.setFieldValue(`config.steps.${index}.status`, checked)
                                  }
                                  height={24}
                                  width={48}
                                  uncheckedIcon={false}
                                  checkedIcon={false}
                                  onColor="#198754"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="row mt-3">
                            <div className="col-md-12">
                              <CustomInput
                                name={`config.steps.${index}.text`}
                                label="Step Description"
                                placeholder="Enter step description..."
                                as="textarea"
                                rows={3}
                                className="form-control mb-2"
                                labelclassname="form-label fw-bold"
                              />
                            </div>
                          </div>

                          <div className="row mt-3">
                            <div className="col-md-12">
                              <label className="form-label fw-bold">Step Image</label>
                              <SimpleImageUpload
                                currentImage={step.image?.location || step.image}
                                onImageChange={(imageData) => {
                                  formik.setFieldValue(`config.steps.${index}.image`, imageData?.location || imageData);
                                }}
                                height="200px"
                              />
                              <small className="text-muted">
                                Recommended: 640x350px. Supports JPG, PNG, WebP
                              </small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}

                    <Button
                      variant="success"
                      onClick={() => {
                        const nextOrder = formik.values.config?.steps?.length || 0;
                        push({
                          stepNumber: nextOrder + 1,
                          text: '',
                          image: '',
                          order: nextOrder,
                          status: true,
                        });
                      }}
                      className="w-100"
                    >
                      <i className="fas fa-plus me-2"></i>Add New Step
                    </Button>
                  </>
                )}
              </FieldArray>
            </Card.Body>
          </Card>

          {/* CTA Section */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <i className="fas fa-mouse-pointer me-2"></i>Call to Action (CTA)
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6">
                  <CustomInput
                    name="config.cta.text"
                    label="Button Text"
                    placeholder="START FREE CONSULTATION"
                    className="form-control"
                    labelclassname="form-label fw-bold"
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    name="config.cta.link"
                    label="Button Link (URL)"
                    placeholder="/category?type=medication&scrollto=weight-loss"
                    className="form-control"
                    labelclassname="form-label fw-bold"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </>
      );
    }

    if (section.sectionKey === 'comparison') {
      return (
        <>
          <div className="row mb-3">
            <div className="col-md-6">
              <CustomInput
                name="config.title"
                label="Title"
                placeholder="Why Choose Ageless?"
                className="form-control"
                labelclassname="form-label"
              />
            </div>
            <div className="col-md-6">
              <CustomInput
                name="config.description"
                label="Description"
                placeholder="Experience more..."
                className="form-control"
                labelclassname="form-label"
              />
            </div>
          </div>

          <h4>Features</h4>
          <FieldArray name="config.features">
            {({ push, remove }) => (
              <>
                {formik.values.config?.features?.map((feature, index) => (
                  <div key={index} className="card mb-3 p-3">
                    <div className="row">
                      <div className="col-md-6">
                        <CustomInput
                          name={`config.features.${index}.feature`}
                          label="Feature Name"
                          placeholder="Feature description"
                          className="form-control mb-2"
                          labelclassname="form-label"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Try Ageless</label>
                        <div>
                          <ReactSwitch
                            checked={feature.tryageless}
                            onChange={(checked) =>
                              formik.setFieldValue(`config.features.${index}.tryageless`, checked)
                            }
                            height={20}
                            width={40}
                            uncheckedIcon={false}
                            checkedIcon={false}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Others</label>
                        <div>
                          <ReactSwitch
                            checked={feature.others}
                            onChange={(checked) =>
                              formik.setFieldValue(`config.features.${index}.others`, checked)
                            }
                            height={20}
                            width={40}
                            uncheckedIcon={false}
                            checkedIcon={false}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <CustomInput
                          name={`config.features.${index}.order`}
                          label="Order"
                          type="number"
                          className="form-control mb-2"
                          labelclassname="form-label"
                        />
                      </div>
                    </div>
                    <Button variant="danger" size="sm" onClick={() => remove(index)}>
                      Remove Feature
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  onClick={() =>
                    push({
                      feature: '',
                      tryageless: true,
                      others: false,
                      order: formik.values.config?.features?.length || 0,
                    })
                  }
                >
                  Add Feature
                </Button>
              </>
            )}
          </FieldArray>
        </>
      );
    }

    if (section.sectionKey === 'testimonials') {
      return (
        <>
          {/* Section Header */}
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-primary bg-opacity-10 border-0">
              <h5 className="mb-0">
                <i className="fas fa-heading me-2 text-primary"></i>
                Section Header
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <CustomInput
                    name="config.subtitle"
                    label="Subtitle"
                    placeholder="Results You Can See"
                    className="form-control"
                    labelclassname="form-label fw-bold"
                  />
                  <small className="text-muted">Small text above the title</small>
                </div>
                <div className="col-md-6 mb-3">
                  <CustomInput
                    name="config.title"
                    label="Main Title"
                    placeholder="Feedback You Can Trust"
                    className="form-control"
                    labelclassname="form-label fw-bold"
                  />
                  <small className="text-muted">Large heading text</small>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Testimonials Section */}
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-info bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-comments me-2 text-info"></i>
                Testimonials
              </h5>
              <Badge bg="info" text="dark">
                {formik.values.config?.testimonials?.length || 0} testimonials
              </Badge>
            </Card.Header>
            <Card.Body>
              <FieldArray name="config.testimonials">
                {({ push, remove, move }) => (
                  <>
                    {formik.values.config?.testimonials?.map((testimonial, index) => (
                      <Card key={index} className="mb-3 shadow-sm border">
                        <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <Badge bg="info" pill>#{index + 1}</Badge>
                            <strong>{testimonial.name || 'Unnamed'}</strong>
                            {testimonial.verified && (
                              <Badge bg="success">
                                <i className="fas fa-check-circle me-1"></i>Verified
                              </Badge>
                            )}
                          </div>
                          <div className="d-flex gap-2">
                            {index > 0 && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => move(index, index - 1)}
                                title="Move up"
                              >
                                <i className="fas fa-arrow-up"></i>
                              </Button>
                            )}
                            {index < formik.values.config.testimonials.length - 1 && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => move(index, index + 1)}
                                title="Move down"
                              >
                                <i className="fas fa-arrow-down"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => remove(index)}
                              title="Remove"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <div className="row">
                            <div className="col-md-8 mb-3">
                              <label className="form-label fw-bold">
                                <i className="fas fa-user me-2 text-primary"></i>
                                Customer Name
                              </label>
                              <CustomInput
                                name={`config.testimonials.${index}.name`}
                                placeholder="e.g., Sarah Johnson"
                                className="form-control"
                              />
                            </div>
                            <div className="col-md-4 mb-3">
                              <label className="form-label fw-bold">
                                <i className="fas fa-star me-2 text-primary"></i>
                                Rating (1-5)
                              </label>
                              <CustomInput
                                name={`config.testimonials.${index}.rating`}
                                type="number"
                                min="1"
                                max="5"
                                placeholder="5"
                                className="form-control"
                              />
                            </div>
                            <div className="col-md-12 mb-3">
                              <label className="form-label fw-bold">
                                <i className="fas fa-comment me-2 text-primary"></i>
                                Review Text
                              </label>
                              <textarea
                                name={`config.testimonials.${index}.review`}
                                placeholder="Amazing service! Professional and caring..."
                                className="form-control"
                                rows="3"
                                value={testimonial.review || ''}
                                onChange={formik.handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-bold">
                                <i className="fas fa-calendar me-2 text-primary"></i>
                                Date/Timeframe
                              </label>
                              <CustomInput
                                name={`config.testimonials.${index}.date`}
                                placeholder="2 weeks ago"
                                className="form-control"
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-bold d-block">
                                <i className="fas fa-check-circle me-2 text-primary"></i>
                                Verified Customer
                              </label>
                              <ReactSwitch
                                uncheckedIcon={false}
                                checkedIcon={false}
                                onChange={(checked) =>
                                  formik.setFieldValue(`config.testimonials.${index}.verified`, checked)
                                }
                                checked={testimonial.verified || false}
                                height={24}
                                width={48}
                                onColor="#28a745"
                              />
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}

                    <Button
                      variant="success"
                      onClick={() =>
                        push({
                          name: '',
                          rating: 5,
                          review: '',
                          date: '',
                          verified: false,
                        })
                      }
                      className="w-100"
                    >
                      <i className="fas fa-plus me-2"></i>Add Testimonial
                    </Button>
                  </>
                )}
              </FieldArray>
            </Card.Body>
          </Card>

          {/* CTA Section */}
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-primary bg-opacity-10 border-0">
              <h5 className="mb-0">
                <i className="fas fa-hand-pointer me-2 text-primary"></i>
                Call-to-Action Button
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <CustomInput
                    name="config.ctaText"
                    label="Button Text"
                    placeholder="START FREE CONSULTATION"
                    className="form-control"
                    labelclassname="form-label fw-bold"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <CustomInput
                    name="config.ctaLink"
                    label="Button Link"
                    placeholder="/category?type=medication&scrollto=weight-loss"
                    className="form-control"
                    labelclassname="form-label fw-bold"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </>
      );
    }

    return null;
  };

  // Initialize default structure if config is empty
  const getInitialValues = () => {
    if (section.sectionKey === 'how_it_works') {
      return {
        config: section.config || {
          title: 'How it Works',
          steps: [],
          cta: { text: 'START FREE CONSULTATION', link: '/category?type=medication&scrollto=weight-loss' },
        },
      };
    }

    if (section.sectionKey === 'comparison') {
      return {
        config: section.config || {
          title: '',
          description: '',
          features: [],
        },
      };
    }

    if (section.sectionKey === 'testimonials') {
      return {
        config: section.config || {
          subtitle: 'Results You Can See',
          title: 'Feedback You Can Trust',
          testimonials: [],
          ctaText: 'START FREE CONSULTATION',
          ctaLink: '/category?type=medication&scrollto=weight-loss',
        },
      };
    }

    return { config: section.config || {} };
  };

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <section className="content_wrapper product_add_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                Landing Page CMS
              </li>
              <li>{section.sectionName}</li>
            </ol>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h3>{section.sectionName} Management</h3>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={backToList}>
                      <i className="fa fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </div>
              </div>
              <Formik initialValues={getInitialValues()} onSubmit={onSubmit} enableReinitialize>
                {(formik) => (
                  <Form>
                    <div className="middle_main_2">
                      {renderEditor(formik)}

                      <div className="footer_main mt-4">
                        <Button
                          type="button"
                          variant="secondary"
                          className="my-2 me-2"
                          onClick={backToList}
                        >
                          Back
                        </Button>
                        <Button type="submit" className="me-2" disabled={loading}>
                          {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName={section.sectionName}
      />
    </section>
  );
};

export default NestedSectionEditor;
